import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, Sparkles, X } from "lucide-react";

async function throwIfNotOk(res: Response) {
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as any).message || `Request failed (${res.status})`);
  }
  return res;
}

// Schema for validation
const journalFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  content: z.string().min(1, "Content is required"),
  date: z.date(),
  enhanceWithAI: z.boolean().default(true),
  tags: z.array(z.string()).optional(),
});

type JournalFormValues = z.infer<typeof journalFormSchema>;

interface NewJournalEntryFormProps {
  athleteId?: string;
  onSuccess?: () => void;
  entryToEdit?: any;
}

const NewJournalEntryForm = ({ athleteId, onSuccess, entryToEdit }: NewJournalEntryFormProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  
  // Set up form
  const form = useForm<JournalFormValues>({
    resolver: zodResolver(journalFormSchema),
    defaultValues: {
      title: entryToEdit?.title || "",
      content: entryToEdit?.content || "",
      date: entryToEdit?.date ? new Date(entryToEdit.date) : new Date(),
      enhanceWithAI: true,
      tags: entryToEdit?.tags || [],
    },
  });

  // Update form when editing an entry
  useEffect(() => {
    if (entryToEdit) {
      form.reset({
        title: entryToEdit.title,
        content: entryToEdit.content,
        date: new Date(entryToEdit.date),
        enhanceWithAI: entryToEdit.aiEnhanced || false,
        tags: entryToEdit.tags || [],
      });
      
      setTags(entryToEdit.tags || []);
    }
  }, [entryToEdit, form]);

  // Create/Update journal entry mutation
  const journalMutation = useMutation({
    mutationFn: async (values: JournalFormValues) => {
      if (entryToEdit) {
        return apiRequest('PUT', `/api/journal/${entryToEdit.id}`, {
          ...values,
          tags: tags,
        }).then(throwIfNotOk);
      } else {
        return apiRequest('POST', `/api/athletes/${athleteId}/journal`, {
          ...values,
          tags: tags,
        }).then(throwIfNotOk);
      }
    },
    onSuccess: () => {
      toast({
        title: entryToEdit ? "Entry Updated" : "Entry Created",
        description: entryToEdit 
          ? "Journal entry has been updated successfully" 
          : "New journal entry has been created"
      });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes", athleteId, "journal"] });
      if (onSuccess) onSuccess();
    },
    onError: () => {
      toast({
        title: "Error",
        description: entryToEdit 
          ? "Failed to update journal entry" 
          : "Failed to create journal entry",
        variant: "destructive"
      });
    }
  });

  // Form submission handler
  const onSubmit = (values: JournalFormValues) => {
    if (!athleteId && !entryToEdit) {
      toast({
        title: "Athlete Profile Required",
        description: "Please create an athlete profile before creating journal entries.",
        variant: "destructive"
      });
      return;
    }
    
    journalMutation.mutate({
      ...values,
      tags: tags,
    });
  };

  // Add tags (supports comma-separated input)
  const addTag = () => {
    if (tagInput.trim()) {
      // Split by comma and process each tag
      const newTags = tagInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag && !tags.includes(tag));
      
      if (newTags.length > 0) {
        setTags([...tags, ...newTags]);
      }
      setTagInput("");
    }
  };

  // Remove a tag
  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Handle Enter key in tag input
  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <h2 className="text-xl font-bold">
          {entryToEdit ? "Edit Journal Entry" : "New Journal Entry"}
        </h2>
        
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Entry title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="date"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Date</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className="w-full pl-3 text-left font-normal"
                    >
                      {field.value ? (
                        format(field.value, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Content</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Write your journal entry here..." 
                  rows={10}
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="space-y-2">
          <Label>Tags</Label>
          <div className="flex">
            <Input
              placeholder="Add tags separated by commas (e.g., Game Day, Training, Leadership)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              className="flex-grow"
            />
            <Button 
              type="button" 
              variant="outline" 
              onClick={addTag}
              className="ml-2"
            >
              Add
            </Button>
          </div>
          
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {tags.map((tag, index) => (
                <Badge key={index} variant="outline" className="bg-primary/10 text-primary">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-1 rounded-full hover:bg-neutral-200 p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
        
        <FormField
          control={form.control}
          name="enhanceWithAI"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel className="flex items-center">
                  <Sparkles className="h-4 w-4 mr-1 text-primary" />
                  Enhance with AI
                </FormLabel>
                <FormDescription>
                  AI will improve clarity and suggest relevant tags for your entry
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <div className="flex justify-end space-x-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (onSuccess) onSuccess();
            }}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            disabled={journalMutation.isPending}
            className="bg-primary"
          >
            {journalMutation.isPending 
              ? (entryToEdit ? "Updating..." : "Saving...") 
              : (entryToEdit ? "Update Entry" : "Save Entry")
            }
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default NewJournalEntryForm;
